import React, { useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography, FormControlLabel, Checkbox, IconButton, Button, Menu, MenuItem, Divider, TextField, InputAdornment, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { ExpandLessOutlined, ExpandMoreOutlined, CopyAll, Search, Clear, UnfoldMore, UnfoldLess } from '@mui/icons-material';
import RecursiveTreeView from './RecursiveTreeView';
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package';
import { NetworkTreeDto } from '../../interfaces/IAffectedCustomers';
import type { TreeApi } from 'react-arborist';
import { matchesNetworkTreeNode } from './networkTreeSearch';

interface NetworkTreeBoxProps {
  showNetworkTreeBox: boolean;
  loadingNetworkTree: boolean;
  networkTree?: NetworkTreeDto[];
  isAllSelected: boolean;
  toggleAllNodes: (isAllSelected: boolean) => void;
  toggleNodesById: (ids: string[], isChecked: boolean) => void;
  treeExpanded: boolean;
  setTreeExpanded: (expanded: boolean) => void;
  openMenu: boolean;
  anchorEl: HTMLElement | null;
  handleMenuClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleMenuClose: () => void;
  enqueueSnackbar: (message: React.ReactNode, options?: any) => void;
}

type CircuitFilter = 'all' | 'selected' | 'not-selected';

const collectCircuitNumbers = (
  nodes: NetworkTreeDto[],
  filter: CircuitFilter,
  acc: string[],
): void => {
  for (const node of nodes) {
    const isServicePoint = node.type === 'ServicePoint';
    const passesFilter =
      filter === 'all' || (filter === 'selected' ? !!node.isChecked : !node.isChecked);
    if (isServicePoint && node.circuitNumbers && passesFilter) {
      for (const circuit of node.circuitNumbers) acc.push(circuit);
    }
    if (node.childNodes) collectCircuitNumbers(node.childNodes, filter, acc);
  }
};

function NetworkTreeBox ({
  showNetworkTreeBox,
  loadingNetworkTree,
  networkTree,
  isAllSelected,
  toggleAllNodes,
  toggleNodesById,
  treeExpanded,
  setTreeExpanded,
  openMenu,
  anchorEl,
  handleMenuClick,
  handleMenuClose,
  enqueueSnackbar,
}: NetworkTreeBoxProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const treeApiRef = useRef<TreeApi<NetworkTreeDto> | undefined>(undefined);
    const trimmedSearch = searchTerm.trim();
    const isSearchActive = trimmedSearch.length > 0;
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const treeHeight = isSmallScreen ? 420 : 600;
    const treeRowHeight = isSmallScreen ? 44 : 32;
    const treeIndent = isSmallScreen ? 32 : 24;

    const visibleNodesUnderSearch = useMemo(() => {
      if (!isSearchActive || !networkTree) return [] as NetworkTreeDto[];
      const acc: NetworkTreeDto[] = [];
      const visit = (nodes: NetworkTreeDto[]): boolean => {
        let anyMatchHere = false;
        for (const node of nodes) {
          const selfMatch = matchesNetworkTreeNode(node, trimmedSearch);
          const childMatch = node.childNodes ? visit(node.childNodes) : false;
          if (selfMatch || childMatch) {
            acc.push(node);
            anyMatchHere = true;
          }
        }
        return anyMatchHere;
      };
      visit(networkTree);
      return acc;
    }, [networkTree, trimmedSearch, isSearchActive]);

    const effectiveIsAllSelected = useMemo(() => {
      if (!isSearchActive) return isAllSelected;
      if (visibleNodesUnderSearch.length === 0) return false;
      return visibleNodesUnderSearch.every((n) => !!n.isChecked);
    }, [isAllSelected, isSearchActive, visibleNodesUnderSearch]);

    const handleSelectAllChange = () => {
      if (!isSearchActive) {
        toggleAllNodes(isAllSelected);
        return;
      }
      if (visibleNodesUnderSearch.length === 0) return;
      const ids = visibleNodesUnderSearch.map((n) => n.id);
      toggleNodesById(ids, !effectiveIsAllSelected);
    };

    const handleOpenAll = () => treeApiRef.current?.openAll();
    const handleCloseAll = () => treeApiRef.current?.closeAll();
    const handleClearSearch = () => setSearchTerm('');

    const copyCircuitsToClipboard = (filter: CircuitFilter) => {
      if (!networkTree) return;
      const acc: string[] = [];
      collectCircuitNumbers(networkTree, filter, acc);
      navigator.clipboard.writeText(acc.join(', '));
      enqueueSnackbar(
        <Typography variant="body2">Kopiert til utklippstavlen.</Typography>,
        { variant: 'success' },
      );
      handleMenuClose();
    };

    const showEmptyResultMessage = isSearchActive && visibleNodesUnderSearch.length === 0;

  return (
    <>
      {showNetworkTreeBox && (
        <Box
          sx={{
            mt: "0 !important",
            padding: 1,
            minHeight: "100%",
            backgroundColor: "background.paper",
            borderColor: "divider",
            borderRadius: "8px",

            "&:hover": {
              borderColor: "primary.main",
            },

            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderTop: 0
          }}>
          {loadingNetworkTree && (
            <Stack direction="row" sx={{
              alignItems: "center"
            }}>
              <LoadingIndicator size={"xsmall"} />
              <Typography variant="body2">
                Henter nettverkstre...
              </Typography>
            </Stack>
          )}
          {networkTree && networkTree.length > 0 && (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: { sm: 'space-between' },
                }}
              >
                <Stack direction={"row"} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <IconButton
                    aria-label="expand"
                    size="small"
                    onClick={() =>
                      setTreeExpanded(!treeExpanded)
                    }
                  >
                    {treeExpanded ? (
                      <ExpandLessOutlined name="Vis mindre" />
                    ) : (
                      <ExpandMoreOutlined name="Vis mer" />
                    )}
                  </IconButton>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={effectiveIsAllSelected}
                        onChange={handleSelectAllChange}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {isSearchActive ? 'Velg alle (synlige)' : 'Velg alle (nettverkstre)'}
                      </Typography>
                    }
                  />
                  {treeExpanded && (
                    <>
                      <Tooltip title="Åpne alle">
                        <IconButton size="small" onClick={handleOpenAll} aria-label="Åpne alle">
                          <UnfoldMore fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Lukk alle">
                        <IconButton size="small" onClick={handleCloseAll} aria-label="Lukk alle">
                          <UnfoldLess fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                >
                  {treeExpanded && (
                    <TextField
                      size="small"
                      placeholder="Søk i tre (navn, IP, SP, EB)"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' && isSearchActive) {
                          e.preventDefault();
                          handleClearSearch();
                        }
                      }}
                      sx={(t) => ({
                        width: { xs: '100%', sm: 260 },
                        '& .MuiInputBase-root': { height: 36.5 },
                        '& .MuiInputBase-input': { ...t.typography.body2 },
                        '& .MuiInputBase-input::placeholder': { ...t.typography.body2 },
                      })}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: isSearchActive ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={handleClearSearch} aria-label="Tøm søk">
                                <Clear fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        },
                      }}
                    />
                  )}
                  <Button
                    id="basic-button"
                    variant="outlined"
                    aria-controls={
                      openMenu ? "basic-menu" : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={openMenu ? "true" : undefined}
                    onClick={handleMenuClick}
                    startIcon={<CopyAll />}
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                  >
                    Kopier
                  </Button>
                </Stack>
                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => copyCircuitsToClipboard('all')}>
                    <Typography variant="body2">Sambandsnummer (alle)</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => copyCircuitsToClipboard('selected')}>
                    <Typography variant="body2">Sambandsnummer (valgte)</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => copyCircuitsToClipboard('not-selected')}>
                    <Typography variant="body2">Sambandsnummer (ikke valgte)</Typography>
                  </MenuItem>
                </Menu>
              </Stack>

              {treeExpanded && (
                <>
                  <Divider
                    sx={{
                      marginTop: 1,
                      marginBottom: 1,
                      borderStyle: "dashed",
                    }}
                  />
                  {showEmptyResultMessage ? (
                    <Box sx={{ py: 2, px: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Ingen treff på «{trimmedSearch}».
                      </Typography>
                    </Box>
                  ) : (
                    <RecursiveTreeView
                      data={networkTree}
                      onNodesCheck={toggleNodesById}
                      searchTerm={trimmedSearch || undefined}
                      treeRef={treeApiRef}
                      height={treeHeight}
                      rowHeight={treeRowHeight}
                      indent={treeIndent}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default NetworkTreeBox;
